import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useSmartBack } from "../hooks/smartBack";
import InfiniteScroll from "react-infinite-scroll-component";
import { makeStyles, shorthands, Spinner, mergeClasses } from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  Mail20Regular,
  Search20Regular,
  Dismiss16Regular,
  CheckmarkCircle20Regular,
  Warning20Regular,
  Building20Regular,
  Person20Regular,
  ArrowUndo20Regular,
  ArrowForward20Regular,
  Delete20Regular,
  Checkmark20Regular,
  ArrowUpload20Regular,
  Call20Regular,
  Open16Regular,
  PersonAdd20Regular,
} from "@fluentui/react-icons";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { useAuth } from "../context/AuthContext";
import {
  listKontaktdatenCleanup,
  fetchKontaktdatenCounts,
  fetchKontaktdatenTeams,
  moveToCompany,
  moveToPerson,
  keepRow,
  deleteRow,
  bulkDeleteRows,
  undoRow,
  parseImportCsv,
  previewImport,
  commitImport,
  fetchImportBatches,
  listImportItems,
  assignImportItem,
  verwerfenImportItem,
  reopenImportItem,
  type CleanupListe,
  type CleanupStatus,
  type KontaktdatenCleanupRow,
  type KontaktdatenErledigtRow,
  type KontaktdatenCounts,
  type ImportBatchRow,
  type ImportItemRow,
} from "../services/kontaktdatenCleanupService";
import { attachMenschen } from "../services/menschenFuersUnternehmenService";
import { buildApMenschItem, resolveCreatedApId } from "../components/mail/mailContactAssign";
import { contactTypeLabel } from "../utils/contactTypeLabel";
import { bestaetigen } from "../components/redesign/bestaetigen";

const PAGE_SIZE = 30;

/** Segmente der Seite: die drei Bereinigungslisten + der Mailscan-Import (D). */
type ViewListe = CleanupListe | "d";

const LISTEN: Array<{ key: ViewListe; label: string }> = [
  { key: "a", label: "A · Zentrale-Mail am AP → Firma" },
  { key: "b", label: "B · Personen-Mail an Firma → AP" },
  { key: "c", label: "C · Platzhalter löschen" },
  { key: "d", label: "Import (D)" },
];

/** Scan-Label → Vorbelegung des Kommunikationstyps (im UI korrigierbar). */
const importLabelToType = (label: string | null): "phone" | "mobile" | "fax" => {
  const l = (label ?? "").toLowerCase();
  if (l === "mobil" || l === "mobile" || l === "handy") return "mobile";
  if (l === "fax" || l === "telefax") return "fax";
  return "phone";
};

/** Anzeigename „Vorname Nachname" / „Nachname, Vorname" → {lastName, firstName}. */
const splitAnzeigename = (name: string): { lastName: string; firstName: string } => {
  const s = (name ?? "").trim();
  if (!s) return { lastName: "", firstName: "" };
  if (s.includes(",")) {
    const [last, first] = s.split(",", 2);
    return { lastName: last.trim(), firstName: (first ?? "").trim() };
  }
  const parts = s.split(/\s+/);
  if (parts.length === 1) return { lastName: parts[0], firstName: "" };
  return { lastName: parts[parts.length - 1], firstName: parts.slice(0, -1).join(" ") };
};

const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  Kunde: { bg: "#e7f6ec", fg: "#1b5e20" },
  Interessent: { bg: "#f0e6f2", fg: "#4d2051" },
  Altkunde: { bg: "#fdf3d6", fg: "#8a6d00" },
  "Verantwortung fürs Unternehmen": { bg: "#e3eefb", fg: "#1c4f86" },
  // Alter Name desselben Status (bis 29.09.2026).
  "Mensch fürs Unternehmen": { bg: "#e3eefb", fg: "#1c4f86" },
};
const statusChipStyle = (s: string | null | undefined): React.CSSProperties => {
  const c = (s && STATUS_COLORS[s]) || { bg: "#ececec", fg: "#5b5560" };
  return { backgroundColor: c.bg, color: c.fg };
};

const AKTION_LABEL: Record<string, string> = {
  verschoben: "verschoben",
  belassen: "belassen",
  geloescht: "gelöscht",
};

const useStyles = makeStyles({
  root: {
    paddingTop: "24px",
    paddingBottom: "24px",
    paddingLeft: "32px",
    paddingRight: "32px",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    maxWidth: "1100px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
  backBtn: {
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("6px", "12px"),
    backgroundColor: "transparent",
    ...shorthands.border("1px", "solid", "transparent"),
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    cursor: "pointer",
    ...shorthands.borderRadius("6px"),
    ":hover": { backgroundColor: BRAND.bg },
  },
  bar: {
    backgroundColor: BRAND.purple,
    backgroundImage: `linear-gradient(135deg, ${BRAND.purple} 0%, ${BRAND.ink} 100%)`,
    color: BRAND.white,
    ...shorthands.padding("20px", "24px"),
    ...shorthands.borderRadius("10px"),
    boxShadow: "0 12px 24px rgba(45,20,43,0.18)",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  titleRow: { display: "flex", alignItems: "center", columnGap: "12px" },
  titleIcon: {
    width: "40px",
    height: "40px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.1)",
    color: BRAND.gold,
    ...shorthands.borderRadius("999px"),
  },
  title: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.4rem, 3.4vw, 1.9rem)",
    color: BRAND.gold,
    flexGrow: 1,
  },
  goldLine: {
    height: "3px",
    backgroundColor: BRAND.gold,
    ...shorthands.borderRadius("999px"),
    width: "60px",
  },
  subtitle: { color: "rgba(255,255,255,0.85)", fontSize: "0.9rem" },
  filterBar: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    rowGap: "8px",
    flexWrap: "wrap",
  },
  pill: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    ...shorthands.padding("6px", "12px"),
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("999px"),
    ":hover": { backgroundColor: BRAND.bg },
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  pillActive: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.purple),
    ":hover": { backgroundColor: BRAND.purple, color: BRAND.white },
  },
  select: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    ...shorthands.padding("6px", "10px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    minWidth: "180px",
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    flexGrow: 1,
    minWidth: "240px",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("6px", "12px"),
  },
  searchInput: {
    flexGrow: 1,
    appearance: "none",
    ...shorthands.border("0", "solid", "transparent"),
    backgroundColor: "transparent",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    outlineStyle: "none",
  },
  clearBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.padding("4px"),
    ...shorthands.border("none"),
    backgroundColor: "transparent",
    color: BRAND.muted,
    cursor: "pointer",
    ...shorthands.borderRadius("999px"),
    ":hover": { backgroundColor: BRAND.bg, color: BRAND.ink },
  },
  card: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    ...shorthands.padding("16px", "18px"),
    marginBottom: "12px",
    boxShadow: "0 1px 2px rgba(45,20,43,0.04), 0 6px 20px rgba(45,20,43,0.05)",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  headRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    flexWrap: "wrap",
  },
  headIcon: {
    width: "34px",
    height: "34px",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f0e6f2",
    color: BRAND.purple,
    ...shorthands.borderRadius("999px"),
  },
  headName: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.05rem",
    color: BRAND.ink,
  },
  chip: {
    fontSize: "0.72rem",
    fontWeight: 600,
    ...shorthands.padding("2px", "9px"),
    ...shorthands.borderRadius("999px"),
    whiteSpace: "nowrap",
  },
  metaChip: { fontSize: "0.75rem", color: BRAND.muted, whiteSpace: "nowrap" },
  mailRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    rowGap: "6px",
    flexWrap: "wrap",
    fontSize: "0.95rem",
  },
  mailValue: { fontWeight: 700, color: BRAND.ink, overflowWrap: "anywhere" },
  badge: {
    fontSize: "0.72rem",
    fontWeight: 600,
    ...shorthands.padding("2px", "9px"),
    ...shorthands.borderRadius("999px"),
    whiteSpace: "nowrap",
  },
  zielRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    rowGap: "6px",
    flexWrap: "wrap",
    fontSize: "0.88rem",
  },
  zielLabel: { color: BRAND.muted, fontSize: "0.8rem", fontWeight: 600 },
  actionRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    rowGap: "8px",
    flexWrap: "wrap",
    ...shorthands.borderTop("1px", "solid", BRAND.borderDashed),
    paddingTop: "12px",
  },
  primaryBtn: {
    appearance: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("8px", "16px"),
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("8px"),
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.87rem",
    fontWeight: 600,
    ":hover": { backgroundColor: BRAND.ink },
    ":disabled": { opacity: 0.45, cursor: "not-allowed", backgroundColor: BRAND.purple },
  },
  secondaryBtn: {
    appearance: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("8px", "16px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.87rem",
    fontWeight: 600,
    ":hover": { backgroundColor: BRAND.bg, ...shorthands.borderColor(BRAND.purple) },
    ":disabled": { opacity: 0.45, cursor: "not-allowed" },
  },
  dangerBtn: {
    appearance: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("8px", "16px"),
    ...shorthands.border("1px", "solid", "#e5b9b9"),
    ...shorthands.borderRadius("8px"),
    backgroundColor: BRAND.white,
    color: "#8b1c1c",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.87rem",
    fontWeight: 600,
    ":hover": { backgroundColor: "#fdecec", ...shorthands.borderColor("#c23b3b") },
    ":disabled": { opacity: 0.45, cursor: "not-allowed" },
  },
  hint: { fontSize: "0.78rem", color: BRAND.muted },
  rowError: { color: "#8b1c1c", fontSize: "0.82rem" },
  rowOk: {
    color: "#1b5e20",
    fontSize: "0.82rem",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
  },
  empty: {
    color: BRAND.muted,
    fontStyle: "italic",
    paddingTop: "24px",
    paddingBottom: "24px",
    textAlign: "center",
  },
  loadMore: { textAlign: "center", color: BRAND.muted, ...shorthands.padding("12px") },
  errorBox: {
    backgroundColor: "#fdecec",
    color: "#8b1c1c",
    ...shorthands.padding("10px", "14px"),
    ...shorthands.border("1px", "solid", "#f5b3b3"),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
  },
  bulkBar: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    rowGap: "8px",
    flexWrap: "wrap",
    ...shorthands.padding("8px", "0"),
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    backgroundColor: BRAND.white,
    ...shorthands.borderRadius("12px"),
    fontSize: "0.88rem",
  },
  th: {
    textAlign: "left",
    fontSize: "0.72rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: BRAND.muted,
    ...shorthands.padding("8px", "10px"),
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
    whiteSpace: "nowrap",
  },
  td: {
    ...shorthands.padding("8px", "10px"),
    ...shorthands.borderBottom("1px", "solid", "#f0ebf1"),
    verticalAlign: "middle",
  },
});

const KontaktdatenCleanupPage = () => {
  const styles = useStyles();
  const goBack = useSmartBack("/datenbereinigung");
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("contacts:update");

  // Deep-Link ?liste=a|b|c|d (analog dem ?q=-Muster der Ansprechpartner-Seite).
  const [searchParams, setSearchParams] = useSearchParams();
  const listeParam = (searchParams.get("liste") ?? "a").toLowerCase();
  const liste: ViewListe =
    listeParam === "b" ? "b" : listeParam === "c" ? "c" : listeParam === "d" ? "d" : "a";
  const setListe = (l: ViewListe) =>
    setSearchParams((p) => {
      const next = new URLSearchParams(p);
      next.set("liste", l);
      return next;
    });

  const [status, setStatus] = useState<CleanupStatus>("open");
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [teamId, setTeamId] = useState("");
  const [teams, setTeams] = useState<Array<{ id: string; name: string | null }>>([]);
  const [rows, setRows] = useState<Array<KontaktdatenCleanupRow | KontaktdatenErledigtRow>>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<KontaktdatenCounts>({
    a: { open: 0, erledigt: 0 },
    b: { open: 0, erledigt: 0 },
    c: { open: 0, erledigt: 0 },
  });
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Ziel-Auswahl je Karte (A: firmaId, B: personId) — vorbelegt vom Server.
  const [targets, setTargets] = useState<Record<number, number | "">>({});
  // Auswahl fürs Sammel-Löschen (Liste C).
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // ── Mailscan-Import (Liste D) ──────────────────────────────────────────────
  const [batches, setBatches] = useState<ImportBatchRow[]>([]);
  const [batchId, setBatchId] = useState<number | null>(null);
  const [importStatus, setImportStatus] = useState<"offen" | "zugewiesen" | "verworfen">("offen");
  const [importTyp, setImportTyp] = useState<"" | "email-neu" | "telefon-neu">("");
  const [importItems, setImportItems] = useState<ImportItemRow[]>([]);
  const [importTotal, setImportTotal] = useState(0);
  const [importHasMore, setImportHasMore] = useState(false);
  // Ziel-Person je Item (Vorschlag vorbelegt) + korrigierter Telefon-Typ.
  const [importZiel, setImportZiel] = useState<Record<number, number | "">>({});
  const [importTypKorrektur, setImportTypKorrektur] = useState<Record<number, string>>({});
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState<Record<number, boolean>>({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [rowErr, setRowErr] = useState<Record<number, string>>({});
  const [flash, setFlash] = useState<string | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showFlash = useCallback((text: string) => {
    setFlash(text);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), 4000);
  }, []);
  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    },
    [],
  );

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const ac = new AbortController();
    void fetchKontaktdatenTeams(ac.signal)
      .then(setTeams)
      .catch(() => {
        /* ohne Teams entfällt nur das Filter-Dropdown */
      });
    return () => ac.abort();
  }, []);

  const loadCounts = useCallback((team: string) => {
    void fetchKontaktdatenCounts(team || undefined)
      .then(setCounts)
      .catch(() => {
        /* Zähler sind Beiwerk */
      });
  }, []);

  const load = useCallback(
    async (
      l: CleanupListe,
      st: CleanupStatus,
      query: string,
      team: string,
      offset: number,
    ) => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      if (offset === 0) setLoading(true);
      try {
        const { rows: page, total: tot } = await listKontaktdatenCleanup(
          l,
          st,
          query || undefined,
          team || undefined,
          PAGE_SIZE,
          offset,
          ac.signal,
        );
        setRows((prev) => (offset === 0 ? page : [...prev, ...page]));
        if (st === "open") {
          setTargets((prev) => {
            const next = offset === 0 ? {} : { ...prev };
            for (const r of page as KontaktdatenCleanupRow[]) {
              if (next[r.commId] === undefined) {
                next[r.commId] =
                  (l === "a" ? r.zielFirmaId : l === "b" ? r.zielPersonId : null) ?? "";
              }
            }
            return next;
          });
          if (offset === 0) setSelected(new Set());
        }
        setTotal(tot);
        setHasMore(offset + page.length < tot);
        setError(null);
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return;
        setError(e instanceof Error ? e.message : "Laden fehlgeschlagen");
        setHasMore(false);
      } finally {
        if (offset === 0) setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (liste === "d") return; // Der Import (D) lädt über die eigenen Effekte unten.
    void load(liste, status, qDebounced, teamId, 0);
    loadCounts(teamId);
    return () => abortRef.current?.abort();
  }, [liste, status, qDebounced, teamId, load, loadCounts]);

  const fetchMore = useCallback(() => {
    if (liste === "d") return;
    void load(liste, status, qDebounced, teamId, rows.length);
  }, [load, liste, status, qDebounced, teamId, rows.length]);

  // ── Import (D): Batches + Items laden ─────────────────────────────────────

  const loadBatches = useCallback(async (): Promise<ImportBatchRow[]> => {
    try {
      const bs = await fetchImportBatches();
      setBatches(bs);
      setBatchId((cur) => (cur && bs.some((b) => b.id === cur) ? cur : (bs[0]?.id ?? null)));
      return bs;
    } catch {
      return [];
    }
  }, []);

  const loadImportItems = useCallback(
    async (batch: number, st: string, typ: string, query: string, offset: number) => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      if (offset === 0) setLoading(true);
      try {
        const { rows: page, total: tot } = await listImportItems(
          batch,
          st || undefined,
          typ || undefined,
          query || undefined,
          PAGE_SIZE,
          offset,
          ac.signal,
        );
        setImportItems((prev) => (offset === 0 ? page : [...prev, ...page]));
        setImportZiel((prev) => {
          const next = offset === 0 ? {} : { ...prev };
          for (const it of page) {
            if (next[it.id] === undefined) next[it.id] = it.match.person?.id ?? "";
          }
          return next;
        });
        setImportTypKorrektur((prev) => {
          const next = offset === 0 ? {} : { ...prev };
          for (const it of page) {
            if (next[it.id] === undefined) next[it.id] = importLabelToType(it.label);
          }
          return next;
        });
        setImportTotal(tot);
        setImportHasMore(offset + page.length < tot);
        setError(null);
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return;
        setError(e instanceof Error ? e.message : "Laden fehlgeschlagen");
        setImportHasMore(false);
      } finally {
        if (offset === 0) setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (liste !== "d") return;
    void loadBatches();
  }, [liste, loadBatches]);

  useEffect(() => {
    if (liste !== "d" || !batchId) return;
    void loadImportItems(batchId, importStatus, importTyp, qDebounced, 0);
    return () => abortRef.current?.abort();
  }, [liste, batchId, importStatus, importTyp, qDebounced, loadImportItems]);

  const fetchMoreImport = useCallback(() => {
    if (liste !== "d" || !batchId) return;
    void loadImportItems(batchId, importStatus, importTyp, qDebounced, importItems.length);
  }, [liste, batchId, importStatus, importTyp, qDebounced, importItems.length, loadImportItems]);

  const dropImportItem = (id: number) => {
    setImportItems((prev) => prev.filter((it) => it.id !== id));
    setImportTotal((t) => Math.max(0, t - 1));
    void loadBatches();
  };

  /** CSV wählen → Matching-Vorschau → nach Bestätigung Batch anlegen. */
  const onCsvSelected = async (file: File) => {
    setError(null);
    try {
      const text = await file.text();
      const parsed = parseImportCsv(text);
      if (!parsed.length) {
        setError("Die CSV enthält keine Datenzeilen (Header + Zeilen erwartet).");
        return;
      }
      const prev = await previewImport(parsed);
      const neu = prev.zeilen - prev.bereitsImportiert;
      const ok = await bestaetigen({
        titel: "Batch anlegen?",
        text:
          `${file.name}: ${prev.zeilen} Zeilen gelesen – ${neu} neu, ` +
          `${prev.bereitsImportiert} bereits importiert (werden übersprungen).`,
        ja: "Batch anlegen",
      });
      if (!ok) return;
      const res = await commitImport(file.name, parsed);
      showFlash(`Batch angelegt: ${res.angelegt} neue Funde, ${res.uebersprungen} übersprungen.`);
      await loadBatches();
      setBatchId(res.batchId);
      setImportStatus("offen");
    } catch (e) {
      setError(e instanceof Error ? e.message : "CSV-Import fehlgeschlagen");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const dropRow = (commId: number) => {
    setRows((prev) =>
      prev.filter((r) =>
        "journalId" in r ? r.commId !== commId : r.commId !== commId,
      ),
    );
    setSelected((prev) => {
      if (!prev.has(commId)) return prev;
      const next = new Set(prev);
      next.delete(commId);
      return next;
    });
    setTotal((t) => Math.max(0, t - 1));
    loadCounts(teamId);
  };

  const runRowAction = async (
    commId: number,
    fn: () => Promise<unknown>,
    okText: string,
  ) => {
    setBusy((b) => ({ ...b, [commId]: true }));
    setRowErr((m) => {
      const { [commId]: _drop, ...rest } = m;
      return rest;
    });
    try {
      await fn();
      dropRow(commId);
      showFlash(okText);
    } catch (e) {
      setRowErr((m) => ({
        ...m,
        [commId]: e instanceof Error ? e.message : "Aktion fehlgeschlagen",
      }));
    } finally {
      setBusy((b) => ({ ...b, [commId]: false }));
    }
  };

  const runBulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length || bulkBusy) return;
    setBulkBusy(true);
    try {
      const res = await bulkDeleteRows(ids, liste as CleanupListe);
      setRows((prev) => prev.filter((r) => !ids.includes(r.commId as number)));
      setSelected(new Set());
      setTotal((t) => Math.max(0, t - ids.length));
      loadCounts(teamId);
      showFlash(`${res.geloescht} Einträge gelöscht.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Sammel-Löschen fehlgeschlagen");
    } finally {
      setBulkBusy(false);
    }
  };

  const openRows = rows as KontaktdatenCleanupRow[];
  const erledigtRows = rows as KontaktdatenErledigtRow[];

  // ── Karten Liste A / B ─────────────────────────────────────────────────────

  const renderOffenCard = (r: KontaktdatenCleanupRow) => {
    const working = !!busy[r.commId];
    const err = rowErr[r.commId];
    const target = targets[r.commId] ?? "";
    const istA = liste === "a";
    const kandidaten = istA ? (r.kandidatenFirmen ?? []) : [];
    const aps = !istA ? (r.kandidatenAps ?? []) : [];
    const zielKandidat = istA
      ? kandidaten.find((k) => k.firmaId === target)
      : undefined;
    const nameMatchAp = !istA ? aps.find((a) => a.nameMatch) : undefined;

    return (
      <div key={r.commId} className={styles.card}>
        <div className={styles.headRow}>
          <span className={styles.headIcon}>
            {istA ? <Person20Regular /> : <Building20Regular />}
          </span>
          <span className={styles.headName}>{r.kontaktName ?? "—"}</span>
          {istA && (
            <span className={styles.metaChip}>
              AP von {kandidaten.length === 1 ? "1 Firma" : `${kandidaten.length} Firmen`}
            </span>
          )}
          <span className={styles.chip} style={statusChipStyle(r.statusLabel)}>
            {r.statusLabel ?? "ohne Status"}
          </span>
          {r.kundennummer && (
            <span className={styles.metaChip}>Kd.-Nr. {r.kundennummer}</span>
          )}
          {r.verantwortlich && (
            <span className={styles.metaChip}>· {r.verantwortlich}</span>
          )}
        </div>

        <div className={styles.mailRow}>
          <Mail20Regular style={{ color: BRAND.purple, flexShrink: 0 }} />
          <span className={styles.mailValue}>{r.value}</span>
          {r.label && (
            <span className={styles.metaChip}>bisheriges Label: „{r.label}"</span>
          )}
          {istA && r.begruendung === "domain-match" && (
            <span className={styles.badge} style={{ backgroundColor: "#e7f6ec", color: "#1b5e20" }}>
              ✓ Domain-Match
            </span>
          )}
          {istA && r.begruendung === "einzige-firma" && (
            <span className={styles.badge} style={{ backgroundColor: "#e3eefb", color: "#1c4f86" }}>
              einzige verbundene Firma
            </span>
          )}
          {istA && r.begruendung === "mehrere-firmen" && (
            <span className={styles.badge} style={{ backgroundColor: "#fdf3d6", color: "#8a6d00" }}>
              ⚠ mehrere Firmen — Ziel wählen
            </span>
          )}
          {!istA && nameMatchAp && (
            <span className={styles.badge} style={{ backgroundColor: "#e7f6ec", color: "#1b5e20" }}>
              ✓ Namens-Match: {nameMatchAp.name}
            </span>
          )}
          {!istA && !nameMatchAp && (
            <span className={styles.badge} style={{ backgroundColor: "#fdf3d6", color: "#8a6d00" }}>
              ? prüfen — kein AP-Match{r.freemail ? " · Freemail" : ""}
            </span>
          )}
        </div>

        <div className={styles.zielRow}>
          <span className={styles.zielLabel}>{istA ? "Ziel-Firma:" : "Ziel-AP:"}</span>
          {istA ? (
            kandidaten.length > 0 ? (
              <select
                className={styles.select}
                value={target === "" ? "" : String(target)}
                disabled={!canWrite || working}
                onChange={(e) =>
                  setTargets((prev) => ({
                    ...prev,
                    [r.commId]: e.target.value ? Number(e.target.value) : "",
                  }))
                }
              >
                <option value="">— Firma wählen —</option>
                {kandidaten.map((k) => (
                  <option key={k.firmaId} value={k.firmaId}>
                    {k.name ?? `#${k.firmaId}`}
                    {k.domainMatch ? " ✓" : ""}
                  </option>
                ))}
              </select>
            ) : (
              <span className={styles.hint}>keine verbundene Firma gefunden</span>
            )
          ) : aps.length > 0 ? (
            <select
              className={styles.select}
              value={target === "" ? "" : String(target)}
              disabled={!canWrite || working}
              onChange={(e) =>
                setTargets((prev) => ({
                  ...prev,
                  [r.commId]: e.target.value ? Number(e.target.value) : "",
                }))
              }
            >
              <option value="">— AP wählen —</option>
              {aps.map((a) => (
                <option key={a.personId} value={a.personId}>
                  {a.name ?? `#${a.personId}`}
                  {a.nameMatch ? " ✓" : ""}
                </option>
              ))}
            </select>
          ) : (
            <span className={styles.hint}>kein Ansprechpartner an dieser Firma</span>
          )}
          {istA && zielKandidat && !zielKandidat.hatMail && (
            <span className={styles.badge} style={{ backgroundColor: "#e3eefb", color: "#1c4f86" }}>
              ⚑ Firma hat noch keine Mail → wird dort Standard
            </span>
          )}
        </div>

        <div className={styles.actionRow}>
          <button
            type="button"
            className={styles.primaryBtn}
            disabled={!canWrite || working || target === ""}
            onClick={() =>
              void runRowAction(
                r.commId,
                () =>
                  istA
                    ? moveToCompany(r.commId, Number(target))
                    : moveToPerson(r.commId, Number(target)),
                istA
                  ? `„${r.value}" zur Firma verschoben.`
                  : `„${r.value}" zum Ansprechpartner verschoben.`,
              )
            }
          >
            <ArrowForward20Regular style={{ fontSize: 16 }} />
            {istA ? "Zur Firma verschieben" : "Zum AP verschieben"}
          </button>
          <button
            type="button"
            className={styles.secondaryBtn}
            disabled={!canWrite || working}
            onClick={() =>
              void runRowAction(
                r.commId,
                () =>
                  keepRow(
                    r.commId,
                    liste as CleanupListe,
                    // Liste A: die bewusste Entscheidung „bleibt am AP" wird mit
                    // gewählter Firma strukturell dokumentiert (kind + companyId).
                    istA && target !== "" ? Number(target) : undefined,
                  ),
                istA ? `„${r.value}" bleibt am Ansprechpartner.` : `„${r.value}" bleibt an der Firma.`,
              )
            }
          >
            <Checkmark20Regular style={{ fontSize: 16 }} />
            {istA ? "Am AP lassen" : "An Firma lassen"}
          </button>
          <button
            type="button"
            className={styles.dangerBtn}
            disabled={!canWrite || working}
            onClick={() =>
              void runRowAction(
                r.commId,
                () => deleteRow(r.commId, liste as CleanupListe),
                `„${r.value}" gelöscht.`,
              )
            }
          >
            <Delete20Regular style={{ fontSize: 16 }} /> Löschen
          </button>
          {working && <Spinner size="tiny" />}
          {err && <span className={styles.rowError}>{err}</span>}
        </div>
      </div>
    );
  };

  // ── Liste C: kompakte Tabelle mit Sammel-Löschen ──────────────────────────

  const renderListeC = () => {
    const allSelected = openRows.length > 0 && openRows.every((r) => selected.has(r.commId));
    return (
      <div>
        {canWrite && (
          <div className={styles.bulkBar}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.85rem" }}>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={(e) =>
                  setSelected(
                    e.target.checked ? new Set(openRows.map((r) => r.commId)) : new Set(),
                  )
                }
              />
              Alle geladenen auswählen
            </label>
            <button
              type="button"
              className={styles.dangerBtn}
              disabled={selected.size === 0 || bulkBusy}
              onClick={() => void runBulkDelete()}
            >
              <Delete20Regular style={{ fontSize: 16 }} />
              Ausgewählte löschen ({selected.size})
            </button>
            {bulkBusy && <Spinner size="tiny" />}
          </div>
        )}
        <div style={{ overflowX: "auto" }}>
          <table className={styles.table}>
            <thead>
              <tr>
                {canWrite && <th className={styles.th} style={{ width: 34 }} />}
                <th className={styles.th}>Kontakt</th>
                <th className={styles.th}>Wert</th>
                <th className={styles.th}>Grund</th>
                <th className={styles.th}>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {openRows.map((r) => {
                const working = !!busy[r.commId];
                const err = rowErr[r.commId];
                return (
                  <tr key={r.commId}>
                    {canWrite && (
                      <td className={styles.td}>
                        <input
                          type="checkbox"
                          checked={selected.has(r.commId)}
                          onChange={(e) =>
                            setSelected((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(r.commId);
                              else next.delete(r.commId);
                              return next;
                            })
                          }
                        />
                      </td>
                    )}
                    <td className={styles.td}>
                      {r.kontaktName ?? "—"}
                      {r.kontaktTyp && (
                        <span className={styles.metaChip}> · {contactTypeLabel(r.kontaktTyp)}</span>
                      )}
                    </td>
                    <td className={styles.td} style={{ overflowWrap: "anywhere" }}>
                      {r.value || <i>leer</i>}
                    </td>
                    <td className={styles.td}>{r.grund}</td>
                    <td className={styles.td} style={{ whiteSpace: "nowrap" }}>
                      <button
                        type="button"
                        className={styles.dangerBtn}
                        style={{ padding: "4px 10px", fontSize: "0.8rem" }}
                        disabled={!canWrite || working}
                        onClick={() =>
                          void runRowAction(
                            r.commId,
                            () => deleteRow(r.commId, "c"),
                            `„${r.value}" gelöscht.`,
                          )
                        }
                      >
                        Löschen
                      </button>{" "}
                      <button
                        type="button"
                        className={styles.secondaryBtn}
                        style={{ padding: "4px 10px", fontSize: "0.8rem" }}
                        disabled={!canWrite || working}
                        onClick={() =>
                          void runRowAction(
                            r.commId,
                            () => keepRow(r.commId, "c", undefined),
                            `„${r.value}" bleibt bestehen.`,
                          )
                        }
                      >
                        Behalten
                      </button>
                      {working && <Spinner size="tiny" />}
                      {err && <div className={styles.rowError}>{err}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── Erledigt-Tab (Journal) ────────────────────────────────────────────────

  const renderErledigtCard = (r: KontaktdatenErledigtRow) => {
    const commId = r.commId ?? -r.journalId;
    const working = !!busy[commId];
    const err = rowErr[commId];
    return (
      <div key={r.journalId} className={styles.card}>
        <div className={styles.headRow}>
          <span className={styles.headIcon}>
            <Mail20Regular />
          </span>
          <span className={styles.headName}>{r.value ?? "—"}</span>
          <span
            className={styles.chip}
            style={
              r.aktion === "geloescht"
                ? { backgroundColor: "#fdecec", color: "#8b1c1c" }
                : r.aktion === "verschoben"
                  ? { backgroundColor: "#e7f6ec", color: "#1b5e20" }
                  : { backgroundColor: "#e3eefb", color: "#1c4f86" }
            }
          >
            {AKTION_LABEL[r.aktion] ?? r.aktion}
            {r.aktion === "verschoben" && r.targetName ? ` → ${r.targetName}` : ""}
          </span>
          {r.sourceName && <span className={styles.metaChip}>von {r.sourceName}</span>}
          {r.reviewedAt && (
            <span className={styles.metaChip}>
              {new Date(r.reviewedAt).toLocaleString("de-DE", {
                dateStyle: "medium",
                timeStyle: "short",
              })}
            </span>
          )}
        </div>
        <div className={styles.actionRow}>
          <button
            type="button"
            className={styles.secondaryBtn}
            disabled={!canWrite || working || r.commId == null}
            onClick={() =>
              r.commId != null &&
              void runRowAction(
                commId,
                () => undoRow(r.commId as number),
                `„${r.value ?? ""}" wurde rückgängig gemacht.`,
              )
            }
          >
            <ArrowUndo20Regular style={{ fontSize: 16 }} /> Rückgängig
          </button>
          {working && <Spinner size="tiny" />}
          {err && <span className={styles.rowError}>{err}</span>}
        </div>
      </div>
    );
  };

  // ── Import (D): Aktionen + Karte ──────────────────────────────────────────

  const runImportAction = async (
    id: number,
    fn: () => Promise<unknown>,
    okText: string,
  ) => {
    setBusy((b) => ({ ...b, [id]: true }));
    setRowErr((m) => {
      const { [id]: _drop, ...rest } = m;
      return rest;
    });
    try {
      await fn();
      dropImportItem(id);
      showFlash(okText);
    } catch (e) {
      setRowErr((m) => ({
        ...m,
        [id]: e instanceof Error ? e.message : "Aktion fehlgeschlagen",
      }));
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  /**
   * „Als AP anlegen + zuweisen": legt die Person über die bestehende
   * Inline-AP-Anlage an (attach-menschen — E-Mail wird dort bereits als
   * geschäftlich mit kind hinterlegt) und markiert das Item anschließend als
   * zugewiesen (der Assign findet die Adresse als Duplikat → idempotent).
   */
  const anlegenAlsAp = async (it: ImportItemRow) => {
    const { lastName, firstName } = splitAnzeigename(it.name ?? "");
    if (!lastName) throw new Error("Kein Name für die AP-Anlage vorhanden");
    if (!it.firmaId) throw new Error("Keine Firma für die AP-Anlage vorhanden");
    const created = await attachMenschen([
      buildApMenschItem({ firmaId: it.firmaId, lastName, firstName, email: it.wert }),
    ]);
    const personId = resolveCreatedApId(created);
    if (!personId) throw new Error("Ansprechpartner konnte nicht angelegt werden");
    await assignImportItem(it.id, { zielArt: "person", contactId: personId });
  };

  const renderImportItem = (it: ImportItemRow) => {
    const working = !!busy[it.id];
    const err = rowErr[it.id];
    const istEmail = it.typ === "email-neu";
    const ziel = importZiel[it.id] ?? "";
    const typKorrektur = importTypKorrektur[it.id] ?? importLabelToType(it.label);
    const offen = it.status === "offen";
    const firmaAusPayload = it.match.firmaName ?? "";

    return (
      <div key={it.id} className={styles.card}>
        <div className={styles.headRow}>
          <span className={styles.headIcon}>
            {istEmail ? <Mail20Regular /> : <Call20Regular />}
          </span>
          <span className={styles.headName} style={{ overflowWrap: "anywhere" }}>
            {it.wert}
          </span>
          {!istEmail && it.label && <span className={styles.metaChip}>({it.label})</span>}
          {it.name && <span className={styles.metaChip}>„{it.name}"</span>}
          {firmaAusPayload && (
            <span className={styles.chip} style={{ backgroundColor: "#f0e6f2", color: "#4d2051" }}>
              {firmaAusPayload}
            </span>
          )}
          {it.apKandidat && (
            <span className={styles.badge} style={{ backgroundColor: "#e3eefb", color: "#1c4f86" }}>
              AP-Kandidat
            </span>
          )}
          {!offen && (
            <span
              className={styles.chip}
              style={
                it.status === "zugewiesen"
                  ? { backgroundColor: "#e7f6ec", color: "#1b5e20" }
                  : { backgroundColor: "#ececec", color: "#5b5560" }
              }
            >
              {it.status}
            </span>
          )}
          {it.mailWeblink && (
            <a
              href={it.mailWeblink}
              target="_blank"
              rel="noreferrer"
              title={[it.mailBetreff, it.mailDatum].filter(Boolean).join(" · ")}
              style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.8rem", color: BRAND.purple, fontWeight: 600 }}
            >
              Mail öffnen <Open16Regular />
            </a>
          )}
        </div>

        <div className={styles.zielRow}>
          {it.match.duplikat ? (
            <span className={styles.badge} style={{ backgroundColor: "#fdf3d6", color: "#8a6d00" }}>
              bereits bei „{it.match.duplikat.name ?? `#${it.match.duplikat.contactId}`}" hinterlegt
            </span>
          ) : it.match.neuAnlegen ? (
            <span className={styles.badge} style={{ backgroundColor: "#e3eefb", color: "#1c4f86" }}>
              Vorschlag: NEUE Person anlegen + AP bei {firmaAusPayload || "der Firma"}
            </span>
          ) : it.match.person ? (
            <span className={styles.badge} style={{ backgroundColor: "#e7f6ec", color: "#1b5e20" }}>
              Vorschlag: {it.match.person.name ?? `#${it.match.person.id}`}
              {it.match.person.via === "quelle" ? " (via Quell-Adresse)" : " (Namens-Match)"}
            </span>
          ) : (
            <span className={styles.hint}>kein automatischer Vorschlag</span>
          )}
          {it.quelleAdresse && (
            <span className={styles.metaChip}>via {it.quelleAdresse}</span>
          )}
          {!it.match.firmaOk && it.firmaId != null && (
            <span className={styles.badge} style={{ backgroundColor: "#fdecec", color: "#8b1c1c" }}>
              Firma aus dem Scan existiert nicht mehr
            </span>
          )}
        </div>

        {offen && (
          <div className={styles.zielRow}>
            {!istEmail && (
              <>
                <span className={styles.zielLabel}>Übernehmen als:</span>
                <select
                  className={styles.select}
                  style={{ minWidth: 120 }}
                  value={typKorrektur}
                  disabled={!canWrite || working}
                  onChange={(e) =>
                    setImportTypKorrektur((prev) => ({ ...prev, [it.id]: e.target.value }))
                  }
                >
                  <option value="phone">Telefon</option>
                  <option value="mobile">Mobil</option>
                  <option value="fax">Fax</option>
                </select>
              </>
            )}
            <span className={styles.zielLabel}>Person:</span>
            {it.match.personKandidaten.length > 0 ? (
              <select
                className={styles.select}
                value={ziel === "" ? "" : String(ziel)}
                disabled={!canWrite || working}
                onChange={(e) =>
                  setImportZiel((prev) => ({
                    ...prev,
                    [it.id]: e.target.value ? Number(e.target.value) : "",
                  }))
                }
              >
                <option value="">— Person wählen —</option>
                {it.match.personKandidaten.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.name ?? `#${k.id}`}
                  </option>
                ))}
              </select>
            ) : (
              <span className={styles.hint}>keine APs an der Firma</span>
            )}
          </div>
        )}

        <div className={styles.actionRow}>
          {offen ? (
            <>
              {istEmail && it.match.neuAnlegen && it.match.firmaOk && it.name && (
                <button
                  type="button"
                  className={styles.primaryBtn}
                  disabled={!canWrite || working}
                  onClick={() =>
                    void runImportAction(
                      it.id,
                      () => anlegenAlsAp(it),
                      `„${it.name}" angelegt, als AP verknüpft und „${it.wert}" zugewiesen.`,
                    )
                  }
                >
                  <PersonAdd20Regular style={{ fontSize: 16 }} /> Als AP anlegen + zuweisen
                </button>
              )}
              <button
                type="button"
                className={it.match.neuAnlegen ? styles.secondaryBtn : styles.primaryBtn}
                disabled={!canWrite || working || ziel === "" || !!it.match.duplikat}
                onClick={() =>
                  void runImportAction(
                    it.id,
                    () =>
                      assignImportItem(it.id, {
                        zielArt: "person",
                        contactId: Number(ziel),
                        ...(istEmail ? {} : { commType: typKorrektur }),
                      }),
                    `„${it.wert}" der Person zugewiesen.`,
                  )
                }
              >
                <ArrowForward20Regular style={{ fontSize: 16 }} /> An Person zuweisen
              </button>
              <button
                type="button"
                className={styles.secondaryBtn}
                disabled={!canWrite || working || !it.match.firmaOk || !it.firmaId || !!it.match.duplikat}
                onClick={() =>
                  void runImportAction(
                    it.id,
                    () =>
                      assignImportItem(it.id, {
                        zielArt: "firma",
                        contactId: it.firmaId as number,
                        ...(istEmail ? {} : { commType: typKorrektur }),
                      }),
                    `„${it.wert}" der Firma zugewiesen.`,
                  )
                }
              >
                <Building20Regular style={{ fontSize: 16 }} /> An Firma
              </button>
              <button
                type="button"
                className={styles.dangerBtn}
                disabled={!canWrite || working}
                onClick={() =>
                  void runImportAction(
                    it.id,
                    () => verwerfenImportItem(it.id),
                    `„${it.wert}" verworfen.`,
                  )
                }
              >
                <Delete20Regular style={{ fontSize: 16 }} />
                {it.match.duplikat ? "OK — schon vorhanden" : "Verwerfen"}
              </button>
            </>
          ) : it.status === "verworfen" ? (
            <button
              type="button"
              className={styles.secondaryBtn}
              disabled={!canWrite || working}
              onClick={() =>
                void runImportAction(
                  it.id,
                  () => reopenImportItem(it.id),
                  `„${it.wert}" ist wieder offen.`,
                )
              }
            >
              <ArrowUndo20Regular style={{ fontSize: 16 }} /> Wieder öffnen
            </button>
          ) : (
            <span className={styles.hint}>
              zugewiesen{it.resolvedAt ? ` am ${new Date(it.resolvedAt).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })}` : ""} — Rückbau bei Bedarf über die Listen A–C
            </span>
          )}
          {working && <Spinner size="tiny" />}
          {err && <span className={styles.rowError}>{err}</span>}
        </div>
      </div>
    );
  };

  const aktiveCounts = liste === "d" ? { open: 0, erledigt: 0 } : counts[liste];
  const dOffen = batches.reduce((sum, b) => sum + (b.offen ?? 0), 0);
  const aktiverBatch = batches.find((b) => b.id === batchId) ?? null;

  return (
    <div className={styles.root}>
      <button className={styles.backBtn} onClick={goBack}>
        <ArrowLeft20Regular /> Zurück zur Datenbereinigung
      </button>

      <div className={styles.bar}>
        <div className={styles.titleRow}>
          <span className={styles.titleIcon}>
            <Mail20Regular style={{ fontSize: 22 }} />
          </span>
          <h1 className={styles.title}>Kontaktdaten bereinigen</h1>
        </div>
        <div className={styles.goldLine} />
        <span className={styles.subtitle}>
          E-Mail-Adressen an die richtige Stelle: Zentrale-Adressen (info@, kontakt@, …)
          gehören zur Firma, personenhafte Adressen zum Ansprechpartner, Platzhalter
          (nicht@bekannt.de, …) werden gelöscht. Jede Aktion ist über den
          Erledigt-Reiter rückgängig zu machen.
        </span>
      </div>

      <div className={styles.filterBar}>
        <span className={styles.hint}>Medium:</span>
        <button className={mergeClasses(styles.pill, styles.pillActive)}>E-Mail</button>
        <button className={styles.pill} disabled title="Telefon-Bereinigung folgt">
          Telefon (folgt)
        </button>
      </div>

      <div className={styles.filterBar}>
        {LISTEN.map((l) => (
          <button
            key={l.key}
            className={mergeClasses(styles.pill, (liste === l.key) && styles.pillActive)}
            onClick={() => setListe(l.key)}
          >
            {l.key === "d"
              ? `${l.label}${dOffen ? ` (${dOffen})` : ""}`
              : `${l.label} (${counts[l.key as CleanupListe].open})`}
          </button>
        ))}
        {liste === "d" && canWrite && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onCsvSelected(f);
              }}
            />
            <button
              className={styles.pill}
              style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6 }}
              onClick={() => fileRef.current?.click()}
            >
              <ArrowUpload20Regular style={{ fontSize: 15 }} /> CSV hochladen
            </button>
          </>
        )}
      </div>

      {liste === "d" ? (
        <div className={styles.filterBar}>
          {batches.length > 0 && (
            <select
              className={styles.select}
              value={batchId ?? ""}
              onChange={(e) => setBatchId(e.target.value ? Number(e.target.value) : null)}
              title="Import-Batch wählen"
              aria-label="Import-Batch wählen"
            >
              {batches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.dateiname} · offen {b.offen} · zugewiesen {b.zugewiesen} · verworfen {b.verworfen}
                </option>
              ))}
            </select>
          )}
          {(["offen", "zugewiesen", "verworfen"] as const).map((st) => (
            <button
              key={st}
              className={mergeClasses(styles.pill, (importStatus === st) && styles.pillActive)}
              onClick={() => setImportStatus(st)}
            >
              {st === "offen"
                ? `Offen (${aktiverBatch?.offen ?? 0})`
                : st === "zugewiesen"
                  ? `Zugewiesen (${aktiverBatch?.zugewiesen ?? 0})`
                  : `Verworfen (${aktiverBatch?.verworfen ?? 0})`}
            </button>
          ))}
          <select
            className={styles.select}
            style={{ minWidth: 140 }}
            value={importTyp}
            onChange={(e) => setImportTyp(e.target.value as "" | "email-neu" | "telefon-neu")}
            aria-label="Nach Fund-Typ filtern"
          >
            <option value="">alle Funde</option>
            <option value="email-neu">E-Mail-Funde</option>
            <option value="telefon-neu">Telefon-Funde</option>
          </select>
          <div className={styles.searchRow}>
            <Search20Regular style={{ color: BRAND.muted, flexShrink: 0 }} />
            <input
              className={styles.searchInput}
              type="search"
              placeholder="Wert, Name, Quell-Adresse oder Firma suchen …"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {q && (
              <button
                className={styles.clearBtn}
                onClick={() => setQ("")}
                aria-label="Suche zurücksetzen"
              >
                <Dismiss16Regular />
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className={styles.filterBar}>
          <button
            className={mergeClasses(styles.pill, (status === "open") && styles.pillActive)}
            onClick={() => setStatus("open")}
          >
            Offen ({aktiveCounts.open})
          </button>
          <button
            className={mergeClasses(styles.pill, (status === "erledigt") && styles.pillActive)}
            onClick={() => setStatus("erledigt")}
          >
            Erledigt ({aktiveCounts.erledigt})
          </button>
          {teams.length > 0 && (
            <select
              className={styles.select}
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              title="Nach Verantwortlichkeit (BO-Team) filtern"
              aria-label="Nach Verantwortlichkeit filtern"
            >
              <option value="">Alle Verantwortlichen</option>
              <option value="none">Ohne Verantwortlichkeit</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name ?? t.id}
                </option>
              ))}
            </select>
          )}
          <div className={styles.searchRow}>
            <Search20Regular style={{ color: BRAND.muted, flexShrink: 0 }} />
            <input
              className={styles.searchInput}
              type="search"
              placeholder="Adresse, Kontakt oder Kundennummer suchen …"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {q && (
              <button
                className={styles.clearBtn}
                onClick={() => setQ("")}
                aria-label="Suche zurücksetzen"
              >
                <Dismiss16Regular />
              </button>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className={styles.errorBox}>
          <Warning20Regular /> {error}
        </div>
      )}
      {!canWrite && (
        <span className={styles.hint}>
          Nur Leserechte — zum Bereinigen wird die Berechtigung „Kontakte bearbeiten" gebraucht.
        </span>
      )}
      {flash && (
        <div className={styles.rowOk}>
          <CheckmarkCircle20Regular style={{ fontSize: 16 }} /> {flash}
        </div>
      )}

      {/* Eigener Scroll-Container: die App-Shell scrollt nicht mit dem Fenster,
          ohne `scrollableTarget` lädt InfiniteScroll nie nach. `dvh` statt `vh`
          wegen der Adressleiste auf Tablets/Handys. */}
      <div
        id="scrollableKontaktdaten"
        style={{ height: "calc(100dvh - 400px)", minHeight: "260px", overflowY: "auto" }}
      >
        {loading ? (
          <p className={styles.empty}>Lade Einträge…</p>
        ) : liste === "d" ? (
          batches.length === 0 ? (
            <p className={styles.empty}>
              Noch kein Import vorhanden — oben rechts eine Mailscan-CSV hochladen
              (Format wie reports/mailscan-cbb-neue-daten-*.csv).
            </p>
          ) : importItems.length === 0 ? (
            <p className={styles.empty}>
              {qDebounced ? "Kein Fund passt zur Suche." : "Keine Funde mit diesem Status."}
            </p>
          ) : (
            <InfiniteScroll
              dataLength={importItems.length}
              next={fetchMoreImport}
              hasMore={importHasMore}
              loader={<div className={styles.loadMore}>Lade weitere Funde…</div>}
              scrollableTarget="scrollableKontaktdaten"
              style={{ overflow: "visible" }}
            >
              {importItems.map((it) => renderImportItem(it))}
            </InfiniteScroll>
          )
        ) : rows.length === 0 ? (
          <p className={styles.empty}>
            {qDebounced
              ? "Kein Eintrag passt zur Suche."
              : teamId
                ? "Kein Eintrag mit dieser Verantwortlichkeit."
                : status === "open"
                  ? "Diese Liste ist abgearbeitet."
                  : "Noch nichts erledigt."}
          </p>
        ) : status === "erledigt" ? (
          <InfiniteScroll
            dataLength={rows.length}
            next={fetchMore}
            hasMore={hasMore}
            loader={<div className={styles.loadMore}>Lade weitere Einträge…</div>}
            scrollableTarget="scrollableKontaktdaten"
            style={{ overflow: "visible" }}
          >
            {erledigtRows.map((r) => renderErledigtCard(r))}
          </InfiniteScroll>
        ) : liste === "c" ? (
          <InfiniteScroll
            dataLength={rows.length}
            next={fetchMore}
            hasMore={hasMore}
            loader={<div className={styles.loadMore}>Lade weitere Einträge…</div>}
            scrollableTarget="scrollableKontaktdaten"
            style={{ overflow: "visible" }}
          >
            {renderListeC()}
          </InfiniteScroll>
        ) : (
          <InfiniteScroll
            dataLength={rows.length}
            next={fetchMore}
            hasMore={hasMore}
            loader={<div className={styles.loadMore}>Lade weitere Einträge…</div>}
            scrollableTarget="scrollableKontaktdaten"
            style={{ overflow: "visible" }}
          >
            {openRows.map((r) => renderOffenCard(r))}
          </InfiniteScroll>
        )}
      </div>

      {liste === "d"
        ? importTotal > 0 && (
            <span className={styles.hint}>
              {importItems.length} von {importTotal} angezeigt
            </span>
          )
        : total > 0 && (
            <span className={styles.hint}>
              {rows.length} von {total} angezeigt
            </span>
          )}
    </div>
  );
};

export default KontaktdatenCleanupPage;
